// src/redux/riderServiceFeeSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  collection,
  getDocs,
} from "firebase/firestore";

// ─── Types ─────────────────────────────────────────────
export type RideRateType =
  | "perPerson"
  | "perPersonPerKm"
  | "perKm"
  | "perDay"
  | "perCar"
  | "perSeat"
  | "perMinute"
  | "perHour"
  | "perWeek"
  | "perMonth"
  | "perYear"
  | "perSurge"; // base fare + per-km + per-minute + surge, Uber-style

export type TripType = 'oneway' | 'roundtrip' | 'multitrip';

export interface Trip {
  id: string;
  pickup: string;
  dropoff: string;
  pickupLat?: number;
  pickupLng?: number;
  dropoffLat?: number;
  dropoffLng?: number;
  distanceKm?: number;
}

// A tier is "above this subtotal, charge this rate". Tiers must be sorted
// descending by threshold — calculateTieredFee walks top-down and returns
// the first (highest) threshold the subtotal clears.
export interface FeeTier {
  threshold: number;
  rate: number;
}

// ─── Default fee ladders ───────────────────────────────
// These are just the starting defaults — every function below accepts an
// optional `tiers` override so the actual rates can be swapped at runtime
// (e.g. loaded from a Firestore "feeSettings" doc via loadFeeConfig below)
// without redeploying code.

// Platform's cut taken from the RIDER side, added on top of ride cost.
export const DEFAULT_GUEST_FEE_TIERS: FeeTier[] = [
  { threshold: 5000, rate: 0.10 },
  { threshold: 1000, rate: 0.09 },
  { threshold: 500, rate: 0.08 },
  { threshold: 100, rate: 0.07 },
  { threshold: 0, rate: 0.06 },
];

// Platform's commission taken from the ride cost before the driver is paid.
// Named "host" to match the backend (createRideCheckoutSession) — "host"
// here means the vehicle/service provider, i.e. the driver's listing.
export const DEFAULT_HOST_FEE_TIERS: FeeTier[] = [
  { threshold: 5000, rate: 0.18 },
  { threshold: 1000, rate: 0.15 },
  { threshold: 500, rate: 0.12 },
  { threshold: 100, rate: 0.10 },
  { threshold: 0, rate: 0.08 },
];

// NOT a platform deduction. Host and driver are different people — the
// host owns/lists the vehicle, the driver actually drives it. Once the
// platform's hostFee is taken off the top, the HOST pays the DRIVER a
// tiered percentage out of their own remaining (gross) profit. Bigger
// gross-profit rides pay the driver a slightly higher cut, smaller ones
// a lower cut — tune freely, or load dynamically via loadFeeConfig().
export const DEFAULT_DRIVER_PAYOUT_TIERS: FeeTier[] = [
  { threshold: 5000, rate: 0.60 },
  { threshold: 1000, rate: 0.55 },
  { threshold: 500, rate: 0.50 },
  { threshold: 100, rate: 0.45 },
  { threshold: 0, rate: 0.40 },
];

// ─── Generic tiered-fee helper ─────────────────────────
// Shared by guest/host/driver fee calculations so the tier-walking logic
// only has to be correct in one place.
export const calculateTieredFee = (subtotal: number, tiers: FeeTier[]): number => {
  const sorted = [...tiers].sort((a, b) => b.threshold - a.threshold);
  const safeSubtotal = Math.max(0, subtotal);
  const tier = sorted.find((t) => safeSubtotal > t.threshold) ?? sorted[sorted.length - 1];
  const rate = tier?.rate ?? 0;
  return +(safeSubtotal * rate).toFixed(2);
};

// ─── Utility Functions ─────────────────────────────────
export const calculateGuestServiceFee = (
  subtotal: number,
  tiers: FeeTier[] = DEFAULT_GUEST_FEE_TIERS
): number => calculateTieredFee(subtotal, tiers);

export const calculateHostFee = (
  subtotal: number,
  tiers: FeeTier[] = DEFAULT_HOST_FEE_TIERS
): number => calculateTieredFee(subtotal, tiers);

// driverPayoutRate is a PERCENTAGE OF THE HOST'S GROSS PROFIT, not of the
// ride cost — the driver is paid BY THE HOST, not by the platform.
export const calculateDriverPayout = (
  hostGrossProfit: number,
  tiers: FeeTier[] = DEFAULT_DRIVER_PAYOUT_TIERS
): number => calculateTieredFee(hostGrossProfit, tiers);

// Host and driver are TWO DIFFERENT PEOPLE. Two deductions happen in
// sequence, from two different pockets:
//   1) Platform takes hostFee off the top of totalRideCost
//        → leaves hostGrossProfit for the host
//   2) Host pays the driver a tiered % of THAT gross profit
//        → leaves hostProfit (net) for the host, driverProfit for the driver
// The platform is never party to step 2 — it's an internal host↔driver
// split, so it does NOT appear in platformProfit (guestsFee + hostFee only).
export const calculateHostEarnings = (
  total: number,
  hostFeeTiers: FeeTier[] = DEFAULT_HOST_FEE_TIERS,
  driverPayoutTiers: FeeTier[] = DEFAULT_DRIVER_PAYOUT_TIERS
) => {
  const safeTotal = Math.max(0, total);

  // Step 1 — platform's cut of the ride cost.
  const hostFee = calculateTieredFee(safeTotal, hostFeeTiers);
  const hostGrossProfit = +(safeTotal - hostFee).toFixed(2);

  // Step 2 — host pays the driver out of their own gross profit.
  const driverProfit = calculateTieredFee(hostGrossProfit, driverPayoutTiers);
  const hostProfit = +(hostGrossProfit - driverProfit).toFixed(2);

  return { hostFee, hostGrossProfit, driverProfit, hostProfit };
};

// Back-compat alias — old call sites using the previous name keep working.
export const calculateDriverEarnings = calculateHostEarnings;

// ─── Dynamic fee config loader ─────────────────────────
// Optional: pull tier overrides from Firestore (e.g. a single
// "settings/fees" doc) so rates can be changed from an admin panel without
// a redeploy. Falls back to the defaults above for any ladder not present
// in the document, or if the doc itself doesn't exist.
export interface FeeConfig {
  guestFeeTiers: FeeTier[];
  hostFeeTiers: FeeTier[];
  driverPayoutTiers: FeeTier[];
}

export const loadFeeConfig = async (): Promise<FeeConfig> => {
  try {
    const snap = await getDoc(doc(db, "settings", "fees"));
    const data = snap.exists() ? snap.data() : {};
    return {
      guestFeeTiers: Array.isArray(data.guestFeeTiers) ? data.guestFeeTiers : DEFAULT_GUEST_FEE_TIERS,
      hostFeeTiers: Array.isArray(data.hostFeeTiers) ? data.hostFeeTiers : DEFAULT_HOST_FEE_TIERS,
      driverPayoutTiers: Array.isArray(data.driverPayoutTiers) ? data.driverPayoutTiers : DEFAULT_DRIVER_PAYOUT_TIERS,
    };
  } catch {
    // Firestore unreachable / doc missing — fall back to hardcoded defaults
    // rather than blocking the fee calculation entirely.
    return {
      guestFeeTiers: DEFAULT_GUEST_FEE_TIERS,
      hostFeeTiers: DEFAULT_HOST_FEE_TIERS,
      driverPayoutTiers: DEFAULT_DRIVER_PAYOUT_TIERS,
    };
  }
};

// Thunk wrapper so components can dispatch this and store the result if
// they want the current live tiers available in state before booking.
export const fetchFeeConfig = createAsyncThunk(
  "riderServiceFees/fetchFeeConfig",
  async () => loadFeeConfig()
);

// ─── Surge fare helper (base + per-km + per-minute + surge multiplier) ───
export const calculatePerSurgeFare = ({
  baseFare = 0,
  perKmRate = 0,
  perMinuteRate = 0,
  distanceKm = 0,
  durationMinutes = 0,
  surgeMultiplier = 1,
}: {
  baseFare?: number;
  perKmRate?: number;
  perMinuteRate?: number;
  distanceKm?: number;
  durationMinutes?: number;
  surgeMultiplier?: number;
}): number => {
  const safeSurge = Math.max(1, surgeMultiplier);
  const metered = baseFare + perKmRate * distanceKm + perMinuteRate * durationMinutes;
  return +(metered * safeSurge).toFixed(2);
};

// ─── Haversine distance calculator (match backend) ─────
/**
 * Calculate distance in kilometers between two coordinates using Haversine formula.
 * Matches the backend implementation exactly.
 */
export const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
};

// ─── Trip distance calculator ──────────────────────────
/**
 * Calculate total distance from an array of trips.
 * For each trip with valid coordinates, use haversine;
 * otherwise use pre-calculated distanceKm if available.
 * Matches backend implementation.
 */
export const calculateTotalTripDistance = (trips: Trip[]): number => {
  if (!Array.isArray(trips) || trips.length === 0) return 0;

  let totalDistance = 0;
  for (const trip of trips) {
    // If coordinates available, calculate from them (most accurate)
    if (
      trip.pickupLat != null &&
      trip.pickupLng != null &&
      trip.dropoffLat != null &&
      trip.dropoffLng != null
    ) {
      totalDistance += haversineKm(
        trip.pickupLat,
        trip.pickupLng,
        trip.dropoffLat,
        trip.dropoffLng
      );
    }
    // Fallback to pre-calculated if available
    else if (trip.distanceKm) {
      totalDistance += trip.distanceKm;
    }
  }
  return +totalDistance.toFixed(2);
};

// ─── Trip pricing strategy helper ──────────────────────
/**
 * Adjust total distance based on trip type.
 * - oneway: use calculated distance as-is
 * - roundtrip: if 2+ trips provided, sum them; otherwise double the first leg
 * - multitrip: sum all legs (already done by calculateTotalTripDistance)
 */
export const applyTripTypeMultiplier = (
  baseDistance: number,
  tripType: TripType,
  tripsArray?: Trip[]
): number => {
  switch (tripType) {
    case 'roundtrip':
      // If we have 2+ trips explicitly provided (outbound + return), use their sum
      if (Array.isArray(tripsArray) && tripsArray.length >= 2) {
        return calculateTotalTripDistance(tripsArray);
      }
      // Otherwise assume simple round trip: double the distance
      return baseDistance * 2;

    case 'multitrip':
      // Use total of all trips (already summed by calculateTotalTripDistance)
      return baseDistance;

    case 'oneway':
    default:
      return baseDistance;
  }
};

// ─── Base cost calculator (shared by both thunks) ─────
// Centralizing this avoids the two switch statements drifting apart.
export const computeBaseRideCost = ({
  rate,
  rateType,
  distanceKm = 0,
  durationDays = 0,
  durationMinutes = 0,
  durationHours = 0,
  durationWeeks = 0,
  durationMonths = 0,
  durationYears = 0,
  passengers = 1,
  seats = 1,
  baseFare = 0,
  perKmRate = 0,
  perMinuteRate = 0,
  surgeMultiplier = 1,
}: {
  rate: number;
  rateType: RideRateType;
  distanceKm?: number;
  durationDays?: number;
  durationMinutes?: number;
  durationHours?: number;
  durationWeeks?: number;
  durationMonths?: number;
  durationYears?: number;
  passengers?: number;
  seats?: number;
  baseFare?: number;
  perKmRate?: number;
  perMinuteRate?: number;
  surgeMultiplier?: number;
}): number => {
  const safePassengers = Math.max(1, passengers);

  switch (rateType) {
    case "perPerson":
      return rate * safePassengers;
    case "perPersonPerKm":
      return rate * safePassengers * distanceKm;
    case "perKm":
      return rate * distanceKm;
    case "perDay":
      return rate * Math.max(1, durationDays);
    case "perCar":
      return rate;
    case "perSeat":
      return rate * seats;
    case "perMinute":
      return rate * Math.max(0, durationMinutes);
    case "perHour":
      return rate * Math.max(0, durationHours);
    case "perWeek":
      return rate * Math.max(1, durationWeeks);
    case "perMonth":
      return rate * Math.max(1, durationMonths);
    case "perYear":
      return rate * Math.max(1, durationYears);
    case "perSurge":
      return calculatePerSurgeFare({
        baseFare,
        perKmRate,
        perMinuteRate,
        distanceKm,
        durationMinutes,
        surgeMultiplier,
      });
    default:
      return rate * distanceKm;
  }
};

// ─── Earnings Calculation with Trip Support ──────────────
export const calculateRideEarnings = createAsyncThunk(
  "riderServiceFees/calculateRideEarnings",
  async ({
    rate,
    rateType,
    distanceKm = 0,
    durationDays = 0,
    durationMinutes = 0,
    durationHours = 0,
    durationWeeks = 0,
    durationMonths = 0,
    durationYears = 0,
    passengers = 1,
    seats = 1,
    extraCharges = 0,
    discountApplied = {},
    creditsAvailable = 0,
    baseFare = 0,
    perKmRate = 0,
    perMinuteRate = 0,
    surgeMultiplier = 1,
    // NEW: Trip-aware pricing
    tripType = 'oneway' as TripType,
    trips = [] as Trip[],
    // Optional dynamic tier overrides — pass these in (e.g. from
    // loadFeeConfig()/state.riderServiceFees.feeConfig) to price using
    // live rates instead of the hardcoded defaults.
    guestFeeTiers = DEFAULT_GUEST_FEE_TIERS,
    hostFeeTiers = DEFAULT_HOST_FEE_TIERS,
    driverPayoutTiers = DEFAULT_DRIVER_PAYOUT_TIERS,
  }: {
    rate: number;
    rateType: RideRateType;
    distanceKm?: number;
    durationDays?: number;
    durationMinutes?: number;
    durationHours?: number;
    durationWeeks?: number;
    durationMonths?: number;
    durationYears?: number;
    passengers?: number;
    seats?: number;
    extraCharges?: number;
    discountApplied?: {
      is5?: boolean;
      is10?: boolean;
      is25?: boolean;
      is50?: boolean;
      is80?: boolean;
    };
    creditsAvailable?: number;
    baseFare?: number;
    perKmRate?: number;
    perMinuteRate?: number;
    surgeMultiplier?: number;
    // NEW: Trip type and trips array
    tripType?: TripType;
    trips?: Trip[];
    guestFeeTiers?: FeeTier[];
    hostFeeTiers?: FeeTier[];
    driverPayoutTiers?: FeeTier[];
  }) => {
    // Calculate total distance from trips if provided, otherwise use distanceKm
    let finalDistanceKm = distanceKm;
    let tripsSummary: Trip[] = [];

    if (Array.isArray(trips) && trips.length > 0) {
      tripsSummary = trips;
      const calculatedDistance = calculateTotalTripDistance(trips);
      finalDistanceKm = applyTripTypeMultiplier(calculatedDistance, tripType, trips);
    }

    let discountPercent = 0;
    if (discountApplied.is80)      discountPercent = 0.8;
    else if (discountApplied.is50) discountPercent = 0.5;
    else if (discountApplied.is25) discountPercent = 0.25;
    else if (discountApplied.is10) discountPercent = 0.1;
    else if (discountApplied.is5)  discountPercent = 0.05;

    const baseRideCost = computeBaseRideCost({
      rate,
      rateType,
      distanceKm: finalDistanceKm,  // Use calculated/adjusted distance
      durationDays,
      durationMinutes,
      durationHours,
      durationWeeks,
      durationMonths,
      durationYears,
      passengers,
      seats,
      baseFare,
      perKmRate,
      perMinuteRate,
      surgeMultiplier,
    });

    const discountedRideCost = baseRideCost * (1 - discountPercent);
    const totalRideCost = Math.max(0, discountedRideCost + extraCharges);

    const guestsFee = calculateGuestServiceFee(totalRideCost, guestFeeTiers);
    const creditsUsed = Math.min(creditsAvailable, totalRideCost);
    const rideAfterCredits = totalRideCost - creditsUsed;

    const totalCharged = +(rideAfterCredits + guestsFee).toFixed(2);

    // hostFee        — platform's commission, taken off totalRideCost
    // hostGrossProfit — what's left for the host before paying the driver
    // driverProfit   — the driver's payout, a tiered % of hostGrossProfit
    //                   (paid BY the host, not by the platform)
    // hostProfit     — the host's final net, after paying the driver
    const { hostFee, hostGrossProfit, driverProfit, hostProfit } = calculateHostEarnings(
      totalRideCost,
      hostFeeTiers,
      driverPayoutTiers
    );

    // Platform only ever collects guestsFee + hostFee. The driver's payout
    // is an internal host↔driver split and never touches platformProfit.
    const platformProfit = +(guestsFee + hostFee).toFixed(2);
    const fullCost = +(totalRideCost + guestsFee).toFixed(2);

    return {
      // Existing fields
      rateType,
      baseRideCost,
      discountedRideCost,
      totalRideCost,
      guestsFee,
      hostFee,
      hostGrossProfit,
      driverProfit,
      hostProfit,
      platformProfit,
      creditsAvailable,
      totalCharged,
      creditsUsed,
      fullCost,
      // NEW: Trip-aware fields
      tripType,
      tripsCount: tripsSummary.length,
      totalDistanceKm: finalDistanceKm,
      trips: tripsSummary,
    };
  }
);

// ─── Batch Final Prices ───────────────────────────────
export const calculateFinalPricesForAllRides = createAsyncThunk(
  "riderServiceFees/calculateFinalPricesForAllRides",
  async (
    {
      distanceKm,
      durationDays = 0,
      durationMinutes = 0,
      durationHours = 0,
      durationWeeks = 0,
      durationMonths = 0,
      durationYears = 0,
      passengers,
      surgeMultiplier = 1,
      tripType = 'oneway' as TripType,
      trips = [] as Trip[],
      guestFeeTiers = DEFAULT_GUEST_FEE_TIERS,
    }: {
      distanceKm: number;
      durationDays?: number;
      durationMinutes?: number;
      durationHours?: number;
      durationWeeks?: number;
      durationMonths?: number;
      durationYears?: number;
      passengers: number;
      surgeMultiplier?: number;
      tripType?: TripType;
      trips?: Trip[];
      guestFeeTiers?: FeeTier[];
    },
    { rejectWithValue }
  ) => {
    try {
      // Calculate total distance from trips if provided
      let finalDistanceKm = distanceKm;
      if (Array.isArray(trips) && trips.length > 0) {
        const calculatedDistance = calculateTotalTripDistance(trips);
        finalDistanceKm = applyTripTypeMultiplier(calculatedDistance, tripType, trips);
      }

      const snapshot = await getDocs(collection(db, "rides"));

      const results: Record<string, { totalCharged: number; fullCost: number; distanceKm: number }> = {};

      snapshot.forEach((docSnap) => {
        const listing = docSnap.data();
        const listingId = docSnap.id;

        const rate: number = listing.rate || 0;
        const rateType: RideRateType = listing.rateType || "perKm";
        const discounts = listing.discounts || {};

        let discountPercent = 0;
        if (discounts.is80)      discountPercent = 0.8;
        else if (discounts.is50) discountPercent = 0.5;
        else if (discounts.is25) discountPercent = 0.25;
        else if (discounts.is10) discountPercent = 0.1;
        else if (discounts.is5)  discountPercent = 0.05;

        const baseRideCost = computeBaseRideCost({
          rate,
          rateType,
          distanceKm: finalDistanceKm,
          durationDays,
          durationMinutes,
          durationHours,
          durationWeeks,
          durationMonths,
          durationYears,
          passengers,
          seats: listing.seats || 1,
          baseFare: listing.baseFare || 0,
          perKmRate: listing.perKmRate || 0,
          perMinuteRate: listing.perMinuteRate || 0,
          surgeMultiplier,
        });

        const discountedRideCost = Math.max(0, baseRideCost * (1 - discountPercent));
        const guestsFee = calculateGuestServiceFee(discountedRideCost, guestFeeTiers);

        results[listingId] = {
          totalCharged: +(discountedRideCost + guestsFee).toFixed(2),
          fullCost: +(discountedRideCost + guestsFee).toFixed(2),
          distanceKm: finalDistanceKm,
        };
      });

      return results;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────
const riderServiceFeeSlice = createSlice({
  name: "riderServiceFees",
  initialState: {
    totalCharged: 0,
    platformProfit: 0,
    creditsAvailable: 0,
    hostFee: 0,
    hostGrossProfit: 0,
    hostProfit: 0,
    baseRideCost: 0,
    discountedRideCost: 0,
    totalRideCost: 0,
    guestsFee: 0,
    driverProfit: 0,
    creditsUsed: 0,
    fullCost: 0,
    // NEW: Trip-aware state
    tripType: 'oneway' as TripType,
    tripsCount: 1,
    totalDistanceKm: 0,
    trips: [] as Trip[],
    // Existing state
    finalPricesByListing: {} as Record<string, { totalCharged: number; fullCost: number; distanceKm: number }>,
    monthlyEarningsData: {} as Record<string, number>,
    monthlyEarnings: {},
    // Live tier overrides — populated by fetchFeeConfig, undefined until
    // loaded (components should fall back to the DEFAULT_*_TIERS exports
    // until this resolves).
    feeConfig: null as FeeConfig | null,
  },
  reducers: {
    resetEarnings: (state) => {
      Object.assign(state, riderServiceFeeSlice.getInitialState());
    },
    clearFinalPrices: (state) => {
      state.finalPricesByListing = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateRideEarnings.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
    });
    builder.addCase(
      calculateFinalPricesForAllRides.fulfilled,
      (state, action) => {
        state.finalPricesByListing = action.payload;
      }
    );
    builder.addCase(fetchFeeConfig.fulfilled, (state, action) => {
      state.feeConfig = action.payload;
    });
  },
});

export const { resetEarnings, clearFinalPrices } =
  riderServiceFeeSlice.actions;
export default riderServiceFeeSlice.reducer;